import aspida from '@aspida/axios'
import { message } from 'antd'
import Axios, { type AxiosError } from 'axios'
import axiosRetry from 'axios-retry'

import { ERROR_MESSAGES } from '@/constants/common/message.constants'

import api, { type ApiInstance } from './aspida/$api'
import {
  authenticationRequestInterceptor,
  sentryErrorInterceptor,
  unauthorizedResponseInterceptor
} from './interceptors'

const axiosInstance = Axios.create()

/**
 * WAFでブロックされることが確認されているHTMLタグのリスト
 */
const WAF_BLOCKED_TAGS = [
  'link',
  'frame',
  'script',
  'iframe',
  'object',
  'embed',
  'svg',
  'style',
  'meta',
  'base',
  'import'
] as const

// WAFでブロックされる特定のHTMLタグが含まれているかチェック
const containsWAFBlockedTag = (params: unknown): boolean => {
  if (!params) return false

  const jsonString = JSON.stringify(params).toLowerCase()

  return WAF_BLOCKED_TAGS.some((tag) => {
    // <tag で始まるパターンを検出（後ろに何が続いても良い）
    const pattern = new RegExp(`<${tag}`, 'i')
    return pattern.test(jsonString)
  })
}

// HTMLタグを検知（<の後に英字が続くパターン）
const containsHTMLTag = (params: unknown): boolean => {
  if (!params) return false

  const jsonString = JSON.stringify(params)
  const pattern = /<[a-zA-Z]/

  return pattern.test(jsonString)
}

/**
 * ネットワークエラーのみリトライを実施する。
 */
axiosRetry(axiosInstance, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    const config = error.config
    const method = config?.method?.toLowerCase()

    const retryableMethods = ['get', 'head', 'options', 'post', 'put', 'delete']

    return (
      typeof method === 'string' &&
      retryableMethods.includes(method) &&
      axiosRetry.isNetworkError(error)
    )
  }
})

export { axiosInstance }

export const apiClient = api(
  aspida(axiosInstance, {
    baseURL: import.meta.env.VITE_BASE_URL,
    paramsSerializer: {}
  })
).api

axiosInstance.interceptors.request.use(authenticationRequestInterceptor)

// Sentryエラーキャプチャ（全環境で有効）
axiosInstance.interceptors.response.use(undefined, sentryErrorInterceptor)

// WAFブロックエラーのハンドリング
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (
      error.code === 'ERR_NETWORK' &&
      error.request?.status === 0 &&
      error.config &&
      containsHTMLTag(error.config.params)
    ) {
      // WAFでブロックされることが確認されているタグをチェック
      const isWAFBlocked =
        containsWAFBlockedTag(error.config.params) ||
        containsWAFBlockedTag(error.config.data)

      if (isWAFBlocked) {
        message.error({
          content: ERROR_MESSAGES.SEARCH_CONDITION_ERROR,
          key: 'waf-error'
        })
      } else {
        message.error({
          content: ERROR_MESSAGES.NETWORK_ERROR,
          key: 'network-error'
        })
      }

      return Promise.resolve({
        data: {
          data: []
        },
        status: 200,
        statusText: 'OK',
        headers: {},
        config: error.config
      })
    }
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) =>
    await unauthorizedResponseInterceptor(
      error,
      axiosInstance,
      apiClient as ApiInstance['api']
    )
)
